import { useEffect, useRef, useState } from "react";
import { devError, devWarn } from "@/lib/log";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import {
  WhatsappLogo,
  Storefront,
  ShieldCheck,
  Tag,
  ShoppingBag,
  CaretLeft,
  CaretRight,
  X,
  CreditCard,
  ShareNetwork,
  Sparkle,
  Check,
  EnvelopeSimple,
  PaperPlaneTilt,
  QrCode,
  DownloadSimple,
  Copy,
  ArrowLeft,
} from "@phosphor-icons/react";
import { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "sonner";
import ChatWidget from "@/components/ChatWidget";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { BankTransferModal } from "@/components/public/BankTransferModal";
import { DigitalTicket } from "@/components/public/DigitalTicket";
import { QRShareModal } from "@/components/public/QRShareModal";
import { SubscribeBlock } from "@/components/public/SubscribeBlock";
import { BACKEND, Field, PUB, PaymentBanner, SocialLink, Trust, getSourcePostId } from "@/components/public/shared";

export default function PublicProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromInternal = location.state?.from || null;
  const [data, setData] = useState(null);
  useDocumentTitle(data?.nombre || "Producto");
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("manual"); // manual | stripe
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState({
    comprador_nombre: "",
    comprador_telefono: "",
    comprador_email: "",
    direccion: "",
    cantidad: 1,
    notas: "",
  });
  const [done, setDone] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [referral, setReferral] = useState(null); // {code, discount_pct}
  const [refLoading, setRefLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showBank, setShowBank] = useState(false);

  // Load product
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${PUB}/products/${id}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Producto no encontrado");
      }
    })();
  }, [id]);

  // Restore unlocked referral discount + record incoming ?ref= visits
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incoming = params.get("ref");
    if (incoming) {
      axios.post(`${BACKEND}/api/referrals/${incoming}/visit`).catch(() => {});
    }
    // Track clicks from social-media posts: link looks like /p/<id>?p=<post_id>
    const incomingPost = params.get("p");
    if (incomingPost) {
      axios.post(`${BACKEND}/api/posts/${incomingPost}/click`).catch(() => {});
      // Persist for sale attribution — survives Stripe redirect roundtrip.
      try {
        sessionStorage.setItem(`vendeia_src_post_${id}`, incomingPost);
      } catch (e) {
        devWarn("sessionStorage write failed", e);
      }
    }
    try {
      const stored = localStorage.getItem(`vendeia_ref_${id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.code && parsed?.discount_pct) {
          // Validate it's still usable
          axios
            .get(`${BACKEND}/api/referrals/${parsed.code}`)
            .then((r) => {
              if (r.data?.valid) setReferral(parsed);
              else localStorage.removeItem(`vendeia_ref_${id}`);
            })
            .catch(() => localStorage.removeItem(`vendeia_ref_${id}`));
        }
      }
    } catch (e) {
      devError("PublicProduct init error", e);
    }
  }, [id]);

  // Handle return from Stripe (?session_id=... or ?cancelled=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled")) {
      setPaymentStatus({ state: "cancelled", message: "Pago cancelado. Puedes intentarlo de nuevo." });
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const sid = params.get("session_id");
    if (!sid) return;
    setPaymentStatus({ state: "checking", message: "Verificando tu pago…" });
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data: st } = await axios.get(`${BACKEND}/api/payments/checkout/status/${sid}`);
        if (st.payment_status === "paid") {
          setPaymentStatus({
            state: "paid",
            message: "¡Pago confirmado! Tu pedido fue registrado.",
            sale_id: st.sale_id,
            amount_total: st.amount_total,
            currency: st.currency,
          });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (st.status === "expired") {
          setPaymentStatus({ state: "failed", message: "La sesión expiró. Intenta nuevamente." });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts || st.status === "processing") {
          setPaymentStatus({
            state: "processing",
            message:
              st.warning ||
              "Stripe está procesando tu pago. Recibirás confirmación por correo cuando se complete.",
            sale_id: st.sale_id,
          });
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPaymentStatus({
          state: "processing",
          message: "Tu pago se está procesando. Te avisaremos por correo cuando se confirme.",
        });
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6 bg-white">
        <div>
          <div className="overline">404</div>
          <h1 className="h-display text-3xl font-bold mt-2">{error}</h1>
          <p className="text-muted2 mt-2 text-sm">El producto que buscas ya no está disponible.</p>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted2 font-mono">
        Cargando…
      </div>
    );

  const moneda = data.negocio?.moneda || "MXN";
  const wa = (data.negocio?.whatsapp_numero || "").replace(/[^0-9]/g, "");
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/p/${data.slug || data.id}` : "";
  const imgs = data.imagenes_base64?.filter(Boolean) || [];
  const hero = imgs[imgIdx];

  // The backend's /public/products endpoint returns the list of methods the
  // seller actually has configured. We render ONLY those buttons so the buyer
  // never lands on a broken Stripe / WhatsApp / transferencia button.
  const methods = data.payment_methods || ["apartar"];
  const has = (k) => methods.includes(k);
  const bank = data.negocio?.bank || null;
  const socials = data.negocio?.socials || {};

  const waMessage = encodeURIComponent(
    `Hola, me interesa el producto: ${data.nombre} (${fmt(data.precio_venta, moneda)}). Lo vi aquí: ${publicUrl}`,
  );
  const waLink = wa ? `https://wa.me/${wa}?text=${waMessage}` : null;

  const fireWhatsappIntent = async () => {
    // Best-effort — never block the WhatsApp redirect on this request.
    try {
      await axios.post(`${BACKEND}/api/whatsapp/intent`, {
        product_id: data.id,
        source: "public_product",
        source_post_id: getSourcePostId(id),
      });
    } catch (e) {
      // Log for diagnostics — intent tracking is non-critical so we don't
      // surface this to the buyer or block the wa.me redirect.
      devWarn("whatsapp intent log failed:", e?.message || e);
    }
  };

  const fireBankIntent = async () => {
    try {
      await axios.post(`${BACKEND}/api/bank/intent`, {
        product_id: data.id,
        source: "public_product",
        source_post_id: getSourcePostId(id),
      });
    } catch (e) {
      devWarn("bank intent log failed:", e?.message || e);
    }
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!order.comprador_nombre.trim()) return toast.error("Tu nombre es obligatorio");
    setSubmitting(true);
    try {
      if (mode === "stripe") {
        const { data: cs } = await axios.post(`${BACKEND}/api/payments/checkout`, {
          ...order,
          product_id: data.id,
          cantidad: Number(order.cantidad) || 1,
          origin_url: window.location.origin,
          referral_code: referral?.code || null,
          source_post_id: getSourcePostId(id),
        });
        if (cs.url) {
          window.location.href = cs.url;
          return;
        }
        toast.error("No se pudo iniciar el pago");
      } else {
        const { data: sale } = await axios.post(`${BACKEND}/api/sales`, {
          ...order,
          product_id: data.id,
          cantidad: Number(order.cantidad) || 1,
          canal: mode === "bank" ? "transferencia" : "web",
          referral_code: referral?.code || null,
          source_post_id: getSourcePostId(id),
        });
        setDone(sale);
        if (referral) {
          localStorage.removeItem(`vendeia_ref_${id}`);
          setReferral(null);
        }
        // Clear attribution: once a sale is created the post got its credit.
        try { sessionStorage.removeItem(`vendeia_src_post_${id}`); } catch (_) { /* noop */ }
        toast.success("Pedido registrado");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo procesar");
    } finally {
      setSubmitting(false);
    }
  };

  const claimReferral = async () => {
    if (referral) return; // already unlocked
    setRefLoading(true);
    try {
      const { data: ref } = await axios.post(`${BACKEND}/api/referrals/share`, {
        product_id: id,
        canal: "share",
      });
      const shareUrl = `${window.location.origin}/p/${data?.slug || id}?ref=${ref.code}`;
      const shareData = {
        title: data.nombre,
        text: `Mira este producto: ${data.nombre} — ${fmt(data.precio_venta, moneda)}`,
        url: shareUrl,
      };
      let shared = false;
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          shared = true;
        } catch (err) {
          if (err?.name === "AbortError") {
            toast.info("Cancelado");
            return;
          }
        }
      }
      if (!shared) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success("Enlace copiado al portapapeles");
          shared = true;
        } catch {
          toast.error("No se pudo compartir. Copia el enlace manualmente.");
          return;
        }
      }
      // Unlock discount
      const unlocked = { code: ref.code, discount_pct: ref.discount_pct };
      localStorage.setItem(`vendeia_ref_${id}`, JSON.stringify(unlocked));
      setReferral(unlocked);
      toast.success(`¡${ref.discount_pct}% de descuento desbloqueado!`);
    } catch (e) {
      toast.error("No se pudo generar el descuento");
    } finally {
      setRefLoading(false);
    }
  };

  const openForm = (m) => {
    setMode(m);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-white text-ink">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {fromInternal && (
              <button
                type="button"
                onClick={() => navigate(fromInternal)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 text-sm font-medium hover:bg-zinc-50 mr-1 shrink-0"
                data-testid="public-back-btn"
                data-help="Vuelve a la sección anterior."
                title="Volver"
              >
                <ArrowLeft size={14} weight="bold" /> <span className="hidden sm:inline">Volver</span>
              </button>
            )}
            <Storefront size={20} className="text-klein shrink-0" />
            <div className="h-display text-lg font-bold tracking-tight truncate">{data.negocio?.nombre || "Tienda"}</div>
          </div>
          {has("whatsapp") && wa && waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={fireWhatsappIntent}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-sm font-medium hover:bg-[#1eb858]"
              data-testid="header-wa"
            >
              <WhatsappLogo size={16} weight="fill" /> {data.negocio.whatsapp_numero}
            </a>
          )}
        </div>
      </header>

      {paymentStatus && (
        <PaymentBanner status={paymentStatus} onClose={() => setPaymentStatus(null)} moneda={moneda} />
      )}

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 grid grid-cols-1 md:grid-cols-12 gap-8">
        <section className="md:col-span-7">
          <div
            className="relative aspect-square w-full bg-zinc-50 border border-zinc-200 overflow-hidden cursor-zoom-in"
            onClick={() => imgs.length && setZoom(true)}
            data-testid="hero-image"
          >
            {hero ? (
              <img src={`data:image/jpeg;base64,${hero}`} alt={data.nombre} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted2 text-sm">Sin imagen</div>
            )}
            {imgs.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setImgIdx((imgIdx - 1 + imgs.length) % imgs.length);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 border border-zinc-200 p-2 hover:bg-klein hover:text-white"
                  data-testid="prev-img"
                >
                  <CaretLeft size={18} weight="bold" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setImgIdx((imgIdx + 1) % imgs.length);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 border border-zinc-200 p-2 hover:bg-klein hover:text-white"
                  data-testid="next-img"
                >
                  <CaretRight size={18} weight="bold" />
                </button>
                <div className="absolute bottom-2 left-2 bg-white/90 border border-zinc-200 px-2 py-0.5 text-[11px] font-mono">
                  {imgIdx + 1}/{imgs.length}
                </div>
              </>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="grid grid-cols-6 gap-2 mt-3">
              {imgs.map((b64, i) => (
                <button
                  key={`${b64?.slice(-16) || "img"}-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`aspect-square overflow-hidden border ${
                    i === imgIdx ? "border-klein ring-2 ring-klein" : "border-zinc-200"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={`data:image/jpeg;base64,${b64}`} alt={`${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="md:col-span-5">
          <div className="overline text-klein flex items-center gap-1.5">
            <Tag size={12} /> {data.categoria}
          </div>
          <h1 className="h-display text-3xl lg:text-4xl font-black tracking-tighter mt-2">{data.nombre}</h1>
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">{data.condicion}</span>
            <span
              className={`px-2 py-0.5 ${
                data.disponibles > 0
                  ? "bg-[#E8F5E9] text-[#1B5E20] border border-[#C8E6C9]"
                  : "bg-[#FFEBEE] text-[#B71C1C] border border-[#FFCDD2]"
              }`}
            >
              {data.disponibles > 0 ? `${data.disponibles} disponibles` : "Agotado"}
            </span>
          </div>

          <div className="mt-5">
            <div className="overline">Precio</div>
            {referral ? (
              <div className="mt-1">
                <div className="flex items-baseline gap-3">
                  <div className="h-display text-5xl font-black tracking-tighter text-klein">
                    {fmt(data.precio_venta * (1 - referral.discount_pct / 100), moneda)}
                  </div>
                  <div className="text-lg text-muted2 line-through tabular-nums">
                    {fmt(data.precio_venta, moneda)}
                  </div>
                </div>
                <div className="mt-2 text-xs text-[#1B5E20] font-medium flex items-center gap-1.5">
                  <Check size={14} weight="bold" /> {referral.discount_pct}% de descuento aplicado
                </div>
              </div>
            ) : (
              <div className="h-display text-5xl font-black tracking-tighter text-klein mt-1">
                {fmt(data.precio_venta, moneda)}
              </div>
            )}
          </div>

          {data.descuento_referido_pct > 0 && !referral && (
            <button
              onClick={claimReferral}
              disabled={refLoading}
              className="mt-4 w-full inline-flex items-center justify-between gap-3 px-4 py-3 border-2 border-dashed border-klein bg-klein/5 hover:bg-klein/10 text-klein transition-colors disabled:opacity-50"
              data-testid="share-discount"
            >
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="fill" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Mejor precio garantizado</div>
                  <div className="text-[11px] opacity-80">
                    Comparte y obtén {data.descuento_referido_pct}% de descuento
                  </div>
                </div>
              </div>
              <ShareNetwork size={18} />
            </button>
          )}

          {referral && (
            <div className="mt-4 p-3 border border-[#00C853] bg-[#E8F5E9] flex items-start gap-2" data-testid="discount-badge">
              <Sparkle size={18} className="text-[#00C853] shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#1B5E20]">
                  {referral.discount_pct}% desbloqueado · código <span className="font-mono">{referral.code}</span>
                </div>
                <div className="text-[11px] text-[#1B5E20]/80 mt-0.5">
                  Se aplica automáticamente al pagar. ¡Gracias por compartir!
                </div>
              </div>
            </div>
          )}

          {data.descripcion && <p className="text-sm text-muted2 mt-4 leading-relaxed">{data.descripcion}</p>}

          {data.para_que_sirve && (
            <div className="mt-4 p-3 bg-zinc-50 border-l-2 border-klein">
              <div className="overline">Para qué sirve</div>
              <div className="text-sm mt-1">{data.para_que_sirve}</div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-2">
            {has("stripe") && (
              <button
                onClick={() => openForm("stripe")}
                disabled={data.disponibles <= 0}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-klein text-white text-sm font-semibold hover:bg-klein-dark disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="buy-card"
              >
                <CreditCard size={18} weight="fill" /> Pagar con tarjeta
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              {has("whatsapp") && waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={fireWhatsappIntent}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1eb858]"
                  data-testid="buy-whatsapp"
                >
                  <WhatsappLogo size={18} weight="fill" /> WhatsApp
                </a>
              )}
              {has("bank") && (
                <button
                  type="button"
                  onClick={() => {
                    fireBankIntent();
                    setShowBank(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0D47A1] text-white text-sm font-semibold hover:bg-[#0a3a85]"
                  data-testid="buy-bank"
                >
                  <ShieldCheck size={18} weight="fill" /> Transferencia
                </button>
              )}
              <button
                onClick={() => openForm("manual")}
                disabled={data.disponibles <= 0}
                className={`inline-flex items-center justify-center gap-2 px-4 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed ${
                  has("whatsapp") || has("bank") ? "" : "col-span-2"
                }`}
                data-testid="buy-form"
              >
                <ShoppingBag size={18} /> Apartar
              </button>
            </div>
            {has("stripe") && (
              <p className="text-[11px] text-muted2 text-center">
                Pago seguro procesado por Stripe · Tarjetas y débito · Sin guardar datos en este sitio
              </p>
            )}
            {!has("stripe") && !has("whatsapp") && !has("bank") && (
              <p
                className="text-[11px] text-[#B71C1C] text-center border border-[#FFCDD2] bg-[#FFEBEE] px-2 py-1.5"
                data-testid="no-payment-methods"
              >
                El vendedor aún no configura métodos de pago. Sólo puedes apartar el producto.
              </p>
            )}

            {(socials.instagram || socials.facebook || socials.tiktok || socials.marketplace) && (
              <div
                className="mt-3 flex flex-wrap gap-2 justify-center"
                data-testid="public-socials"
              >
                {socials.instagram && (
                  <SocialLink
                    handle={socials.instagram}
                    base="https://instagram.com/"
                    label="Instagram"
                    testid="social-instagram"
                  />
                )}
                {socials.facebook && (
                  <SocialLink
                    handle={socials.facebook}
                    base="https://facebook.com/"
                    label="Facebook"
                    testid="social-facebook"
                  />
                )}
                {socials.tiktok && (
                  <SocialLink
                    handle={socials.tiktok}
                    base="https://tiktok.com/@"
                    label="TikTok"
                    testid="social-tiktok"
                  />
                )}
                {socials.marketplace && (
                  <SocialLink
                    handle={socials.marketplace}
                    base=""
                    label="Marketplace"
                    testid="social-marketplace"
                  />
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowQR(true)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-300 text-sm font-medium hover:bg-zinc-50 w-full"
              data-testid="btn-share-qr"
            >
              <QrCode size={16} weight="bold" /> Compartir · QR · Volante imprimible
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 border-t border-l border-zinc-200">
            <Trust icon={<ShieldCheck size={16} weight="fill" />} title="Pago seguro" />
            <Trust icon={<Tag size={16} />} title="Precio fijo" />
            <Trust icon={<Storefront size={16} />} title="Vendedor verificado" />
          </div>
        </section>
      </main>

      <footer className="border-t border-zinc-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
          <SubscribeBlock productId={data.id} negocio={data.negocio} />
        </div>
        <div className="border-t border-zinc-200">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 text-xs text-muted2 flex flex-wrap justify-between gap-2">
            <span>© {new Date().getFullYear()} {data.negocio?.nombre || "VendeIA"}</span>
            <span className="font-mono">Powered by VendeIA</span>
          </div>
        </div>
      </footer>

      {zoom && hero && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)}>
          <button className="absolute top-4 right-4 text-white p-2 hover:bg-white/10" onClick={() => setZoom(false)}>
            <X size={20} weight="bold" />
          </button>
          <img src={`data:image/jpeg;base64,${hero}`} alt={data.nombre} className="max-h-[90vh] max-w-full object-contain" />
        </div>
      )}

      {showForm && !done && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            className="bg-white border border-zinc-200 max-w-md w-full p-6 animate-fade-in max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitOrder}
            data-testid="order-form"
          >
            <div className="overline text-klein flex items-center gap-1.5">
              {mode === "stripe" ? <CreditCard size={12} weight="fill" /> : <ShoppingBag size={12} />}
              {mode === "stripe" ? "Pago con tarjeta" : mode === "bank" ? "Confirmar transferencia" : "Apartar producto"}
            </div>
            <h3 className="h-display text-2xl font-bold tracking-tight mt-1 mb-4">
              {mode === "stripe" ? "Confirma tus datos" : mode === "bank" ? "Avísanos de tu transferencia" : "Confirma tu pedido"}
            </h3>
            <div className="space-y-3">
              <Field label="Nombre completo *">
                <input
                  className="input-flat"
                  required
                  value={order.comprador_nombre}
                  onChange={(e) => setOrder({ ...order, comprador_nombre: e.target.value })}
                  data-testid="order-name"
                />
              </Field>
              <Field label={mode === "stripe" ? "Email *" : "Email (opcional)"}>
                <input
                  type="email"
                  required={mode === "stripe"}
                  className="input-flat"
                  value={order.comprador_email}
                  onChange={(e) => setOrder({ ...order, comprador_email: e.target.value })}
                  data-testid="order-email"
                />
              </Field>
              <Field label="Teléfono / WhatsApp">
                <input
                  className="input-flat"
                  value={order.comprador_telefono}
                  onChange={(e) => setOrder({ ...order, comprador_telefono: e.target.value })}
                  data-testid="order-phone"
                />
              </Field>
              <Field label="Dirección de envío (opcional)">
                <textarea
                  className="input-flat min-h-[60px]"
                  value={order.direccion}
                  onChange={(e) => setOrder({ ...order, direccion: e.target.value })}
                  data-testid="order-address"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Cantidad">
                  <input
                    type="number"
                    min="1"
                    max={data.disponibles || 1}
                    className="input-flat"
                    value={order.cantidad}
                    onChange={(e) => setOrder({ ...order, cantidad: e.target.value })}
                    data-testid="order-qty"
                  />
                </Field>
                <div>
                  <label className="overline block mb-1.5">Total</label>
                  <div className="text-xl font-bold h-display tabular-nums mt-1">
                    {fmt(
                      (Number(order.cantidad) || 1) *
                        data.precio_venta *
                        (referral ? 1 - referral.discount_pct / 100 : 1),
                      moneda,
                    )}
                  </div>
                  {referral && (
                    <div className="text-[10px] text-[#00C853]">−{referral.discount_pct}%</div>
                  )}
                </div>
              </div>
              <Field label="Notas (opcional)">
                <textarea
                  className="input-flat min-h-[50px]"
                  value={order.notas}
                  onChange={(e) => setOrder({ ...order, notas: e.target.value })}
                  data-testid="order-notes"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost" data-testid="order-cancel">
                Cancelar
              </button>
              <button type="submit" disabled={submitting} className="btn-klein" data-testid="order-submit">
                {submitting
                  ? mode === "stripe"
                    ? "Redirigiendo a Stripe…"
                    : "Enviando…"
                  : mode === "stripe"
                  ? "Continuar al pago"
                  : "Enviar pedido"}
              </button>
            </div>
          </form>
        </div>
      )}

      {done && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-200 max-w-md w-full p-8 text-center">
            <div className="w-12 h-12 mx-auto bg-[#E8F5E9] flex items-center justify-center">
              <ShieldCheck size={24} className="text-[#00C853]" weight="fill" />
            </div>
            <h3 className="h-display text-2xl font-bold tracking-tight mt-4">¡Pedido recibido!</h3>
            <p className="text-sm text-muted2 mt-2">
              Folio <span className="font-mono">{done.id.slice(0, 8)}</span> · El vendedor te contactará pronto.
            </p>
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold w-full hover:bg-[#1eb858]"
              >
                <WhatsappLogo size={18} weight="fill" /> Avisar por WhatsApp
              </a>
            )}
            <button
              onClick={() => {
                setDone(null);
                setShowForm(false);
              }}
              className="mt-2 btn-ghost w-full"
              data-testid="order-close"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {showQR && (
        <QRShareModal
          publicUrl={publicUrl}
          referral={referral}
          productId={data.id}
          nombre={data.nombre}
          precio={data.precio_venta}
          moneda={moneda}
          hero={hero}
          waNumber={wa}
          negocio={data.negocio?.nombre || "Tienda"}
          onClose={() => setShowQR(false)}
        />
      )}

      {showBank && bank && (
        <BankTransferModal
          bank={bank}
          producto={data.nombre}
          precio={data.precio_venta}
          moneda={moneda}
          negocio={data.negocio?.nombre || "Tienda"}
          onClose={() => setShowBank(false)}
          onApartar={() => {
            setShowBank(false);
            openForm("bank");
          }}
        />
      )}

      {paymentStatus?.state === "paid" && (
        <DigitalTicket
          status={paymentStatus}
          data={data}
          moneda={moneda}
          onClose={() => setPaymentStatus(null)}
        />
      )}

      <Toaster richColors position="top-center" />
      <ChatWidget
        productId={data.id}
        productName={data.nombre}
        businessName={data.negocio?.nombre}
      />
    </div>
  );
}
